import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { platformApi } from '@/services/platformApi';

interface AppConfig {
  appName: string;
  ui: {
    showPrepaidCardOption: boolean;
    defaultCurrency: string;
    enableReferralTokens: boolean;
    maxTokenAmount: number;
    minTokenAmount: number;
    supportedCurrencies: string[];
    cardIssuanceRules: Record<string, { min: number; max: number }>;
  };
  features: {
    enableInvoiceTokens: boolean;
    enableMerchantProfiles: boolean;
    enableChatFeatures: boolean;
    enableAnalytics: boolean;
    enableNotifications: boolean;
    nfcPaymentMethod: 'react' | 'native';
    // Navigation visibility flags
    showMarketplace?: boolean;
    showVaiuShell?: boolean;
    showReferralTokens?: boolean;
    showContacts?: boolean;
    showMessages?: boolean;
  };
  limits: {
    dailyTokenLimit: number;
    monthlyTransactionLimit: number;
    maxActiveTokens: number;
    tokenExpiryDays: number;
  };
  integrations: {
    stripeEnabled: boolean;
    paypalEnabled: boolean;
    plaidEnabled: boolean;
    boPaymentsEnabled: boolean;
    bitcoinEnabled: boolean;
  };
  branding: {
    appName: string;
    primaryColor: string;
    logoUrl: string;
    supportEmail: string;
    companyName: string;
  };
  security: {
    requireEmailVerification: boolean;
    enableTwoFactorAuth: boolean;
    sessionTimeoutMinutes: number;
    maxLoginAttempts: number;
  };
}

interface ConfigContextType {
  config: AppConfig | null;
  loading: boolean;
  error: string | null;
  appName: string;
  refreshConfig: () => Promise<void>;
  updateConfig: (newConfig: Partial<Omit<AppConfig, 'appName'>>) => Promise<boolean>;
  updateConfigSection: (section: 'ui' | 'features' | 'limits' | 'integrations' | 'branding' | 'security', sectionConfig: any) => Promise<boolean>;
  setAppName: (name: string) => void;
}

import { getAppNameForDomain } from '@/utils/appConfig';

// Get app name from domain or environment
const getAppName = (): string => {
  return getAppNameForDomain();
};

// Default configuration values (fallback when API is not available)
const defaultConfig: AppConfig = {
  appName: getAppName(),
  ui: {
    showPrepaidCardOption: true,
    defaultCurrency: 'USD',
    enableReferralTokens: true,
    maxTokenAmount: 10000,
    minTokenAmount: 0.01,
    supportedCurrencies: ['USD', 'EUR', 'GBP', 'CAD', 'BTC'],
    cardIssuanceRules: {
      "USD": { min: 0.35, max: 1000 },
      "EUR": { min: 0.35, max: 1000 },
      "GBP": { min: 0.35, max: 1000 },
      "CAD": { min: 0.50, max: 1000 },
      "BTC": { min: 0.00001, max: 1 }
    },
  },
  features: {
    enableInvoiceTokens: true,
    enableMerchantProfiles: true,
    enableChatFeatures: true,
    enableAnalytics: true,
    enableNotifications: true,
    nfcPaymentMethod: 'react',
    // Navigation visibility flags - default to true for backward compatibility
    showMarketplace: true,
    showVaiuShell: true,
    showReferralTokens: true,
    showContacts: true,
    showMessages: true,
  },
  limits: {
    dailyTokenLimit: 50,
    monthlyTransactionLimit: 1000,
    maxActiveTokens: 10,
    tokenExpiryDays: 30,
  },
  integrations: {
    stripeEnabled: true,
    paypalEnabled: true,
    plaidEnabled: true,
    boPaymentsEnabled: true,
    bitcoinEnabled: true,
  },
  branding: {
    appName: 'Vaiu',
    primaryColor: '#3B82F6',
    logoUrl: '/img/logo.png',
    supportEmail: 'support@vaiu.io',
    companyName: 'Vaiu Global Inc.',
  },
  security: {
    requireEmailVerification: true,
    enableTwoFactorAuth: false,
    sessionTimeoutMinutes: 60,
    maxLoginAttempts: 5,
  },
};

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appName, setAppName] = useState(getAppName());

  const refreshConfig = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔧 Fetching app configuration for ${appName} from platform...`);
      const fetchedConfig = await platformApi.getAppConfig(appName);
      console.log('🔧 App configuration loaded:', fetchedConfig);
      // Merge with defaults to ensure all properties are present
      const mergedConfig = {
        ...defaultConfig,
        ...fetchedConfig,
        integrations: {
          ...defaultConfig.integrations,
          ...fetchedConfig.integrations,
        },
        ui: {
          ...defaultConfig.ui,
          ...fetchedConfig.ui,
        },
      };
      setConfig(mergedConfig);
    } catch (err: any) {
      console.warn('🔧 Failed to load app configuration, using defaults:', err.message);
      setError(err.message);
      setConfig(defaultConfig); // Use default config as fallback
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (newConfig: Partial<Omit<AppConfig, 'appName'>>): Promise<boolean> => {
    try {
      console.log(`🔧 Updating app configuration for ${appName}:`, newConfig);
      const result = await platformApi.updateAppConfig(newConfig, appName);
      
      if (result.success) {
        // Merge the new config with existing config
        setConfig(prev => prev ? { ...prev, ...newConfig } : defaultConfig);
        console.log('🔧 App configuration updated successfully');
        return true;
      } else {
        console.error('🔧 Failed to update app configuration:', result.message);
        setError(result.message);
        return false;
      }
    } catch (err: any) {
      console.error('🔧 Error updating app configuration:', err.message);
      setError(err.message);
      return false;
    }
  };

  const updateConfigSection = async (section: 'ui' | 'features' | 'limits' | 'integrations' | 'branding' | 'security', sectionConfig: any): Promise<boolean> => {
    try {
      console.log(`🔧 Updating ${section} configuration for ${appName}:`, sectionConfig);
      const result = await platformApi.updateConfigSection(section, sectionConfig, appName);
      
      if (result.success) {
        // Update only the specific section
        setConfig(prev => prev ? { 
          ...prev, 
          [section]: { ...prev[section], ...sectionConfig }
        } : defaultConfig);
        console.log(`🔧 ${section} configuration updated successfully`);
        return true;
      } else {
        console.error(`🔧 Failed to update ${section} configuration:`, result.message);
        setError(result.message);
        return false;
      }
    } catch (err: any) {
      console.error(`🔧 Error updating ${section} configuration:`, err.message);
      setError(err.message);
      return false;
    }
  };

  // Load configuration on mount and when appName changes
  useEffect(() => {
    refreshConfig();
  }, [appName]);

  const value = {
    config,
    loading,
    error,
    appName,
    refreshConfig,
    updateConfig,
    updateConfigSection,
    setAppName,
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

export type { AppConfig }; 